import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import { getImageUrl, getProductImageUrl } from '../../utils/imageHelper';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminProducts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    supplier: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  // Ouvrir automatiquement le modal d'ajout si on vient de /admin/products/add
  useEffect(() => {
    if (location.pathname === '/admin/products/add') {
      // Attendre que les données soient chargées avant d'ouvrir le modal
      if (!loading && suppliers.length > 0 && categories.length > 0) {
        setShowAddModal(true);
        // Nettoyer l'URL pour éviter de rouvrir le modal à chaque re-render
        navigate('/admin/products', { replace: true });
      }
    }
  }, [location.pathname, loading, suppliers.length, categories.length, navigate]);

  // Fonction pour formater les prix avec séparateurs de milliers
  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0';
    return numPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      // Inverser l'ordre si on clique sur la même colonne
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne : tri croissant par défaut
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = products;

    if (filters.search) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(filters.search.toLowerCase()))
      );
    }

    if (filters.category) {
      filtered = filtered.filter(product => product.category_id === parseInt(filters.category));
    }

    if (filters.supplier) {
      filtered = filtered.filter(product => product.supplier_id === parseInt(filters.supplier));
    }

    if (filters.status) {
      filtered = filtered.filter(product => product.is_active === (filters.status === 'active'));
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'category':
            aValue = (a.category?.name || '').toLowerCase();
            bValue = (b.category?.name || '').toLowerCase();
            break;
          case 'supplier':
            aValue = (a.supplier?.business_name || '').toLowerCase();
            bValue = (b.supplier?.business_name || '').toLowerCase();
            break;
          case 'price':
            aValue = parseFloat(a.price) || 0;
            bValue = parseFloat(b.price) || 0;
            break;
          case 'stock':
            aValue = parseInt(a.stock_quantity) || 0;
            bValue = parseInt(b.stock_quantity) || 0;
            break;
          case 'status':
            aValue = a.is_active ? 1 : 0;
            bValue = b.is_active ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [products, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, suppliersData, categoriesData] = await Promise.all([
        adminApi.getProducts(),
        adminApi.getSuppliers(),
        adminApi.getCategories()
      ]);

      setProducts(Array.isArray(productsData) ? productsData : []);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      addNotification('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    supplier_id: '',
    price: '',
    sale_price: '',
    stock_quantity: '',
    min_stock_alert: '5',
    unit: 'kg',
    weight: '',
    origin: '',
    variety: '',
    quality: 'standard',
    short_description: '',
    description: '',
    is_active: true,
    is_organic: false,
    is_seasonal: false,
    status: 'available'
  });
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      supplier: '',
      status: ''
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        let value = formData[key];
        if (typeof value === 'boolean') {
          value = value ? '1' : '0';
        }
        // S'assurer que null et undefined deviennent des chaînes vides
        // et que 'null' (string) aussi
        if (value === null || value === undefined || value === 'null') {
          value = '';
        }
        // Pour description, s'assurer que c'est toujours une chaîne
        if (key === 'description' && (value === null || value === undefined || value === 'null')) {
          value = '';
        }
        formDataToSend.append(key, value);
      });

      const newImages = images.filter(img => img.file && !img.isExisting);
      newImages.forEach((image) => {
        formDataToSend.append('images[]', image.file);
      });

      let savedProduct;
      if (editingProduct) {
        const response = await adminApi.updateProduct(editingProduct.id, formDataToSend);
        savedProduct = response.data || response;

        if (!savedProduct || !savedProduct.id) {
          throw new Error('Réponse invalide du serveur');
        }

        setProducts(prev => prev.map(product =>
          product.id === editingProduct.id ? savedProduct : product
        ));
        addNotification('Produit modifié avec succès !', 'success');
      } else {
        const response = await adminApi.createProduct(formDataToSend);
        savedProduct = response.data || response;

        if (!savedProduct || !savedProduct.id) {
          throw new Error('Réponse invalide du serveur');
        }

        setProducts(prev => [...prev, savedProduct]);
        addNotification('Produit créé avec succès !', 'success');
      }

      resetForm();
      setShowAddModal(false);
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du produit:', error);
      const errorMessage = error.message || error.error || error.errors || 'Erreur inconnue';
      const errorDetails = typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage;
      addNotification(`Erreur: ${errorDetails}`, 'error');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      category_id: '',
      supplier_id: '',
      price: '',
      sale_price: '',
      stock_quantity: '',
      min_stock_alert: '5',
      unit: 'kg',
      weight: '',
      origin: '',
      variety: '',
      quality: 'standard',
      short_description: '',
      description: '',
      is_active: true,
      is_featured: false,
      is_organic: false,
      is_seasonal: false,
      status: 'available'
    });
    setImages([]);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    setTimeout(() => {
      const newImages = files.map((file, index) => ({
        id: Date.now() + index,
        name: file.name || `image-${index + 1}`,
        url: URL.createObjectURL(file),
        file: file
      }));
      setImages(prev => [...prev, ...newImages]);
      setUploadingImages(false);
    }, 1000);
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      category_id: product.category_id || '',
      supplier_id: product.supplier_id || '',
      price: product.price || '',
      sale_price: product.sale_price || '',
      stock_quantity: product.stock_quantity || '',
      min_stock_alert: product.min_stock_alert || '5',
      unit: product.unit || 'kg',
      weight: product.weight || '',
      origin: product.origin || '',
      variety: product.variety || '',
      quality: product.quality || 'standard',
      short_description: product.short_description || '',
      description: product.description || '',
      is_active: product.is_active !== undefined ? product.is_active : true,
      is_featured: product.is_featured || false,
      is_organic: product.is_organic || false,
      is_seasonal: product.is_seasonal || false,
      status: product.status || 'available'
    });

    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      const existingImages = product.images.map((img, index) => {
        let imageUrl = '';
        if (img.image_path) {
          if (img.image_path.startsWith('http')) {
            imageUrl = img.image_path;
          } else if (img.image_url) {
            imageUrl = img.image_url;
          } else {
            imageUrl = getImageUrl(img.image_path);
          }
        }

        return {
          id: img.id || Date.now() + index,
          name: img.image_path?.split('/').pop() || `image-${index + 1}`,
          url: imageUrl,
          file: null,
          isExisting: true,
          imageId: img.id
        };
      });
      setImages(existingImages);
    } else {
      setImages([]);
    }

    setShowAddModal(true);
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;

    try {
      await adminApi.deleteProduct(selectedProduct.id);
      setProducts(prev => prev.filter(product => product.id !== selectedProduct.id));
      addNotification('Produit supprimé avec succès', 'success');
      setShowDeleteModal(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du produit:', error);
      addNotification('Erreur lors de la suppression', 'error');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const product = products.find(p => p.id === id);
      const updatedProduct = await adminApi.updateProduct(id, {
        ...product,
        is_active: !product.is_active
      });
      setProducts(prev => prev.map(p => p.id === id ? updatedProduct : p));
      addNotification(`Produit ${!product.is_active ? 'activé' : 'désactivé'} avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      addNotification('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  // Fonction pour exporter les produits en CSV
  const handleExport = () => {
    try {
      // Utiliser les produits filtrés pour l'export
      const productsToExport = filteredProducts.length > 0 ? filteredProducts : products;

      if (productsToExport.length === 0) {
        addNotification('Aucun produit à exporter', 'warning');
        return;
      }

      // Préparer les en-têtes CSV
      const headers = [
        'ID',
        'Nom',
        'Description',
        'Catégorie',
        'Fournisseur',
        'Prix (FCFA)',
        'Prix de vente (FCFA)',
        'Stock',
        'Unité',
        'Statut',
        'Actif',
        'Date de création'
      ];

      // Convertir les produits en lignes CSV
      const csvRows = productsToExport.map(product => {
        const row = [
          product.id || '',
          `"${(product.name || '').replace(/"/g, '""')}"`,
          `"${(product.description || '').replace(/"/g, '""')}"`,
          product.category?.name || 'N/A',
          product.supplier?.business_name || 'N/A',
          formatPrice(product.price),
          product.sale_price ? formatPrice(product.sale_price) : '',
          product.stock_quantity || '0',
          product.unit || '',
          product.status || 'available',
          product.is_active ? 'Oui' : 'Non',
          product.created_at ? new Date(product.created_at).toLocaleDateString('fr-FR') : ''
        ];
        return row.join(',');
      });

      // Créer le contenu CSV complet
      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      // Créer un BOM pour UTF-8 (pour Excel)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      // Créer un lien de téléchargement
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Nom du fichier avec date
      const date = new Date().toISOString().split('T')[0];
      const filename = `produits_${date}.csv`;
      link.setAttribute('download', filename);

      // Déclencher le téléchargement
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`${productsToExport.length} produit(s) exporté(s) avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('Erreur lors de l\'export des produits', 'error');
    }
  };

  // Fonction pour obtenir l'URL de l'image principale d'un produit
  const getProductMainImage = (product) => {
    if (product.primary_image) {
      return getProductImageUrl(product.primary_image);
    }
    if (product.images?.[0]) {
      return getProductImageUrl(product.images[0]);
    }
    return null;
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="page-header">
          <h1 className="page-title">Gestion des Produits</h1>
          <p className="page-subtitle">Chargement des données...</p>
        </div>
        <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement des produits...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Gestion</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Produits</span>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Gestion des Produits</h1>
        <p className="page-subtitle">
          Créer et gérer les produits agricoles en les associant aux fournisseurs
        </p>
      </div>

      {/* Actions et Filtres */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto flex-wrap">
          {/* Recherche */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Rechercher un produit..."
              className="search-input pl-10"
            />
          </div>

          {/* Filtre par catégorie */}
          <select
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Toutes les catégories</option>
            {categories.map((category, index) => (
              <option key={category?.id || `cat-${index}`} value={category?.id || ''}>{category?.name || 'Catégorie sans nom'}</option>
            ))}
          </select>

          {/* Filtre par fournisseur */}
          <select
            name="supplier"
            value={filters.supplier}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Tous les fournisseurs</option>
            {suppliers.map((supplier, index) => (
              <option key={supplier?.id || `sup-${index}`} value={supplier?.id || ''}>{supplier?.business_name || 'Fournisseur sans nom'}</option>
            ))}
          </select>

          {/* Filtre par statut */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>

          {/* Bouton pour effacer les filtres */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary text-sm"
          >
            Effacer
          </button>
        </div>

        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="btn btn-primary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Ajouter un Produit
        </button>
      </div>

      {/* Products DataTable */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Liste des Produits
            <span className="text-sm text-gray-500 ml-2">
              ({filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="table-actions">
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              disabled={filteredProducts.length === 0}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Produit
                    {sortField === 'name' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('category')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Catégorie
                    {sortField === 'category' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('supplier')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Fournisseur
                    {sortField === 'supplier' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('price')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Prix
                    {sortField === 'price' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('stock')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Stock
                    {sortField === 'stock' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Statut
                    {sortField === 'status' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-lg font-medium">Aucun produit trouvé</p>
                      <p className="text-sm">Commencez par ajouter votre premier produit</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentProducts.map((product) => {
                  const productImageUrl = getProductMainImage(product);

                  return (
                    <tr key={product.id}>
                      <td>
                        <div className="flex items-center">
                          {/* Miniature de l'image principale */}
                          <div className="relative flex-shrink-0">
                            {productImageUrl ? (
                              <img
                                src={productImageUrl}
                                alt={product.name}
                                className="w-12 h-12 rounded-lg mr-3 border border-gray-200"
                                style={{
                                  objectFit: 'cover',
                                  maxWidth: '48px',
                                  maxHeight: '48px',
                                  minWidth: '48px',
                                  minHeight: '48px',
                                  width: '48px',
                                  height: '48px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const avatar = e.target.nextElementSibling;
                                  if (avatar) avatar.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white font-semibold mr-3 ${productImageUrl ? 'hidden' : ''}`} style={{
                              minWidth: '48px',
                              minHeight: '48px',
                              maxWidth: '48px',
                              maxHeight: '48px'
                            }}>
                              {product.name && product.name.length > 0 ? product.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            {/* Badge pour le nombre d'images */}
                            {product.images && product.images.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center border-2 border-white" style={{
                                fontSize: '10px',
                                lineHeight: '1',
                                padding: '0'
                              }}>
                                {product.images.length}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {product.name || 'Produit sans nom'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {product.description || 'Aucune description'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-info">{product.category?.name || 'N/A'}</span>
                      </td>
                      <td className="font-medium">{product.supplier?.business_name || 'N/A'}</td>
                      <td>
                        <div className="font-medium">{formatPrice(product.price)} FCFA</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          par {product.unit}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center">
                          <span className={`badge ${product.stock_quantity > 50 ? 'badge-success' : product.stock_quantity > 20 ? 'badge-warning' : 'badge-danger'}`}>
                            {product.stock_quantity} {product.unit}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${product.is_active ? 'badge-success' : 'badge-warning'}`}>
                          {product.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Bouton Détails - Bleu/Info */}
                          <button
                            onClick={() => handleViewDetails(product)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#3b82f6' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                            title="Voir les détails"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Détails
                          </button>

                          {/* Bouton Modifier - Jaune/Warning */}
                          <button
                            onClick={() => handleEdit(product)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#eab308' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ca8a04'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#eab308'}
                            title="Modifier le produit"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </button>

                          {/* Bouton Activer/Désactiver - Vert/Orange */}
                          <button
                            onClick={() => toggleStatus(product.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{
                              backgroundColor: product.is_active ? '#f97316' : '#22c55e'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = product.is_active ? '#ea580c' : '#16a34a';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = product.is_active ? '#f97316' : '#22c55e';
                            }}
                            title={product.is_active ? 'Désactiver le produit' : 'Activer le produit'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {product.is_active ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              )}
                            </svg>
                            {product.is_active ? 'Désactiver' : 'Activer'}
                          </button>

                          {/* Bouton Supprimer - Rouge/Danger */}
                          <button
                            onClick={() => handleDeleteClick(product)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#ef4444' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                            title="Supprimer le produit"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-700">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredProducts.length)} sur {filteredProducts.length} produits
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Détails */}
      {showDetailsModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => { setShowDetailsModal(false); setSelectedProduct(null); }}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Détails du Produit</h3>
              <button
                onClick={() => { setShowDetailsModal(false); setSelectedProduct(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations principales */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nom du produit</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedProduct.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-700">{selectedProduct.description || 'Aucune description'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Catégorie</label>
                      <p className="text-gray-700">{selectedProduct.category?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Fournisseur</label>
                      <p className="text-gray-700">{selectedProduct.supplier?.business_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Prix</label>
                      <p className="text-lg font-semibold text-green-600">{formatPrice(selectedProduct.price)} FCFA</p>
                      <p className="text-sm text-gray-500">par {selectedProduct.unit}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Stock</label>
                      <p className={`text-lg font-semibold ${selectedProduct.stock_quantity > 50 ? 'text-green-600' : selectedProduct.stock_quantity > 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {selectedProduct.stock_quantity} {selectedProduct.unit}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Statut</label>
                      <span className={`badge ${selectedProduct.is_active ? 'badge-success' : 'badge-warning'}`}>
                        {selectedProduct.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date de création</label>
                      <p className="text-gray-700">{new Date(selectedProduct.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Images du produit</label>
                  {selectedProduct.images && selectedProduct.images.length > 0 ? (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedProduct.images.map((image, index) => {
                        const imageUrl = getProductImageUrl(image);

                        return (
                          <div key={image.id || index} className="relative group">
                            <img
                              src={imageUrl}
                              alt={`${selectedProduct.name} ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            {image.is_primary && (
                              <span className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                Principale
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>Aucune image disponible</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => { setShowDetailsModal(false); setSelectedProduct(null); }}
                className="btn btn-secondary"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEdit(selectedProduct);
                }}
                className="btn btn-primary"
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmation de Suppression */}
      {showDeleteModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setSelectedProduct(null); }}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title text-red-600">Confirmer la suppression</h3>
            </div>
            <div className="modal-body">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Êtes-vous sûr de vouloir supprimer ce produit ?</p>
                  <p className="text-sm text-gray-500 mt-1">
                    <strong>{selectedProduct.name}</strong> sera définitivement supprimé.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedProduct(null); }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn text-white"
                style={{ backgroundColor: '#ef4444' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { resetForm(); setShowAddModal(false); }}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingProduct ? 'Modifier le Produit' : 'Ajouter un Produit'}
              </h3>
              <button
                onClick={() => { resetForm(); setShowAddModal(false); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Nom du Produit
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="modal-form-input"
                      required
                    />
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Catégorie
                    </label>
                    <select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      className="modal-form-select"
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map((category, index) => (
                        <option key={category?.id || `cat-${index}`} value={category?.id || ''}>{category?.name || 'Catégorie sans nom'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Fournisseur
                    </label>
                    <select
                      name="supplier_id"
                      value={formData.supplier_id}
                      onChange={handleInputChange}
                      className="modal-form-select"
                      required
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers.map((supplier, index) => (
                        <option key={supplier?.id || `sup-${index}`} value={supplier?.id || ''}>{supplier?.business_name || 'Fournisseur sans nom'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Prix (FCFA)
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        min="0"
                        step="10"
                        required
                      />
                    </div>
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Stock
                      </label>
                      <input
                        type="number"
                        name="stock_quantity"
                        value={formData.stock_quantity}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Unité
                    </label>
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      className="modal-form-select"
                      required
                    >
                      <option value="kg">Kilogramme (kg)</option>
                      <option value="g">Gramme (g)</option>
                      <option value="l">Litre (l)</option>
                      <option value="ml">Millilitre (ml)</option>
                      <option value="pièce">Pièce</option>
                      <option value="boîte">Boîte</option>
                      <option value="sac">Sac</option>
                    </select>
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Description courte
                    </label>
                    <input
                      type="text"
                      name="short_description"
                      value={formData.short_description}
                      onChange={handleInputChange}
                      className="modal-form-input"
                      placeholder="Description courte du produit"
                    />
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Description complète
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="modal-form-textarea"
                      rows="3"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Prix de vente (FCFA)
                      </label>
                      <input
                        type="number"
                        name="sale_price"
                        value={formData.sale_price}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        min="0"
                        step="10"
                        placeholder="Prix promotionnel (optionnel)"
                      />
                    </div>
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Alerte stock minimum
                      </label>
                      <input
                        type="number"
                        name="min_stock_alert"
                        value={formData.min_stock_alert}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Poids (kg)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        min="0"
                        step="0.01"
                        placeholder="Poids unitaire"
                      />
                    </div>
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Origine
                      </label>
                      <input
                        type="text"
                        name="origin"
                        value={formData.origin}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        placeholder="Ex: Gabon"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Variété
                      </label>
                      <input
                        type="text"
                        name="variety"
                        value={formData.variety}
                        onChange={handleInputChange}
                        className="modal-form-input"
                        placeholder="Ex: Roma, Golden"
                      />
                    </div>
                    <div className="modal-form-group">
                      <label className="modal-form-label">
                        Qualité
                      </label>
                      <select
                        name="quality"
                        value={formData.quality}
                        onChange={handleInputChange}
                        className="modal-form-select"
                      >
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                        <option value="economy">Économique</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Produit actif
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_featured"
                          checked={formData.is_featured}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          En vedette
                        </span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_organic"
                          checked={formData.is_organic}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Produit bio
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="is_seasonal"
                          checked={formData.is_seasonal}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Saisonnier
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Upload d'images */}
                  <div className="modal-form-group">
                    <label className="modal-form-label">
                      Images du produit
                    </label>
                    <div className="image-upload-area">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <svg className="image-upload-icon" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div>
                          <p className="image-upload-text">
                            {uploadingImages ? 'Upload en cours...' : 'Cliquez pour ajouter des images'}
                          </p>
                          <p className="image-upload-hint">PNG, JPG, GIF jusqu'à 10MB</p>
                        </div>
                      </label>
                    </div>

                    {/* Aperçu des images */}
                    {images.length > 0 && (
                      <div className="image-preview-grid">
                        {images.map((image) => {
                          const imageUrl = image.url || '';
                          const imageName = image.name || 'Image produit';

                          return (
                            <div key={image.id} className="image-preview-item">
                              <img
                                src={imageUrl}
                                alt={imageName}
                                className="image-preview-img"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              {image.isExisting && (
                                <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                  Existant
                                </span>
                              )}
                              <button
                                onClick={() => removeImage(image.id)}
                                className="image-remove-btn"
                                type="button"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowAddModal(false);
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {editingProduct ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
